import { useState, useEffect } from "react";
import { FolderOpen, ChevronRight, Plus, ArrowLeft, Loader2, Check } from "lucide-react";
import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogFooter,
} from "@/components/ui/alert-dialog";
import { Input } from "@/components/ui/input";
import axios from "axios";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function FolderPicker({ open, onClose, destination, onSelect }) {
  const [folders, setFolders] = useState([]);
  const [loading, setLoading] = useState(false);
  const [currentPath, setCurrentPath] = useState("");
  const [pathHistory, setPathHistory] = useState([]);
  const [creating, setCreating] = useState(false);
  const [newFolderName, setNewFolderName] = useState("");

  const serviceName = destination === "gdrive" ? "Google Drive" : "Dropbox";

  useEffect(() => {
    if (open) {
      setCurrentPath("");
      setPathHistory([]);
      fetchFolders("");
    }
  }, [open, destination]);

  const fetchFolders = async (path) => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/folders/${destination}`, { params: { path } });
      if (res.data.error) {
        toast.error(res.data.error);
      } else {
        setFolders(res.data.folders || []);
      }
    } catch {
      toast.error(`Failed to load ${serviceName} folders`);
    } finally {
      setLoading(false);
    }
  };

  const navigateInto = (folder) => {
    setPathHistory(prev => [...prev, { path: currentPath, name: currentPath ? currentPath.split("/").pop() : "Root" }]);
    setCurrentPath(folder.path);
    fetchFolders(folder.path);
  };

  const navigateBack = () => {
    const prev = pathHistory[pathHistory.length - 1];
    if (prev) {
      setPathHistory(h => h.slice(0, -1));
      setCurrentPath(prev.path);
      fetchFolders(prev.path);
    }
  };

  const handleCreateFolder = async () => {
    if (!newFolderName.trim()) return;
    setCreating(true);
    try {
      const res = await axios.post(`${API}/folders/${destination}`, {
        name: newFolderName.trim(),
        parent_path: currentPath,
      });
      if (res.data.error) {
        toast.error(res.data.error);
      } else {
        toast.success(`Created "${newFolderName.trim()}"`);
        setNewFolderName("");
        fetchFolders(currentPath);
      }
    } catch {
      toast.error("Failed to create folder");
    } finally {
      setCreating(false);
    }
  };

  const handleSelect = async (folderPath, folderName) => {
    try {
      await axios.post(`${API}/folder-preference`, {
        destination,
        folder_path: folderPath,
        folder_name: folderName,
      });
      onSelect(folderPath, folderName);
      onClose();
      toast.success(`Saving to "${folderName}"`);
    } catch {
      toast.error("Failed to save preference");
    }
  };

  const currentFolderName = currentPath
    ? (destination === "dropbox" ? currentPath.split("/").pop() : pathHistory.length > 0 ? "Current folder" : "Root")
    : "Root";

  return (
    <AlertDialog open={open} onOpenChange={(v) => { if (!v) onClose(); }}>
      <AlertDialogContent className="border-[#2a2e36] rounded-2xl max-w-md" style={{ background: '#1a1d21' }}>
        <AlertDialogHeader>
          <AlertDialogTitle className="text-lg text-[#f4f4f5] tracking-tight" style={{ fontFamily: "'Playfair Display', Georgia, serif" }}>
            Choose Folder
          </AlertDialogTitle>
          <p className="text-xs text-[#71717a]">Select where to save your videos in {serviceName}</p>
        </AlertDialogHeader>

        <div className="mt-2">
          {/* Navigation */}
          <div className="flex items-center gap-2 mb-3">
            {pathHistory.length > 0 && (
              <button onClick={navigateBack} className="text-[#52525b] hover:text-[#c6a96f] transition-colors p-1">
                <ArrowLeft className="h-4 w-4" />
              </button>
            )}
            <span className="text-[10px] font-mono text-[#52525b] tracking-wider truncate">
              // {destination === "dropbox" ? (currentPath || "/") : currentFolderName}
            </span>
          </div>

          {/* Select current folder button */}
          {currentPath && (
            <button
              onClick={() => handleSelect(currentPath, currentPath.split("/").pop() || "Root")}
              className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg mb-2 text-left bg-[rgba(198,169,111,0.05)] border border-[#c6a96f]/20 hover:border-[#c6a96f]/40 transition-colors"
            >
              <Check className="h-4 w-4 text-[#c6a96f]" />
              <span className="text-sm text-[#c6a96f]">Use this folder</span>
            </button>
          )}

          {/* Folder list */}
          <div className="max-h-60 overflow-y-auto space-y-1">
            {loading ? (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="h-5 w-5 animate-spin text-[#c6a96f]" />
              </div>
            ) : folders.length === 0 ? (
              <p className="text-xs text-[#52525b] text-center py-6 font-mono">// no subfolders here</p>
            ) : (
              folders.map((f) => (
                <button
                  key={f.path}
                  onClick={() => navigateInto(f)}
                  className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-left hover:bg-[#21252b] transition-colors group"
                >
                  <FolderOpen className="h-4 w-4 text-[#52525b] group-hover:text-[#c6a96f] transition-colors flex-shrink-0" />
                  <span className="text-sm text-[#e4e4e7] truncate">{f.name}</span>
                  <ChevronRight className="h-3.5 w-3.5 text-[#3f3f46] ml-auto flex-shrink-0" />
                </button>
              ))
            )}
          </div>

          {/* Create new folder */}
          <div className="mt-3 pt-3 border-t border-[#222630]">
            <div className="flex items-center gap-2">
              <Input
                placeholder="New folder name"
                value={newFolderName}
                onChange={(e) => setNewFolderName(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter") handleCreateFolder(); }}
                className="bg-[#21252b] border-[#2a2e36] text-[#e4e4e7] placeholder:text-[#52525b] focus-visible:ring-[#a08a58] h-9 text-sm rounded-lg flex-1"
              />
              <button
                onClick={handleCreateFolder}
                disabled={!newFolderName.trim() || creating}
                className="btn-gold px-3 py-2 rounded-lg text-xs flex items-center gap-1.5 disabled:opacity-30"
              >
                {creating ? <Loader2 className="h-3 w-3 animate-spin" /> : <Plus className="h-3 w-3" />}
                Create
              </button>
            </div>
          </div>
        </div>

        <AlertDialogFooter className="mt-4">
          <button onClick={onClose} className="btn-ghost px-4 py-2 rounded-xl text-xs" style={{ background: 'transparent' }}>
            Cancel
          </button>
          {!currentPath && (
            <button
              onClick={() => handleSelect("", "Vimeo Backups")}
              className="btn-ghost px-4 py-2 rounded-xl text-xs !border-[#c6a96f]/30 !text-[#c6a96f]"
            >
              Use default (Vimeo Backups)
            </button>
          )}
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
